/* ==========================================================================================
   CE 2026 · ce-dims-range.js · the SIZE range filters (collections builder, launch wave 10-10-2026)
   Tommy asked for size ranges (13-08); PARITY d #6. The live site's dim-range.js (A372) idea, rebuilt for this theme.

   Shopify has no range filter for a metafield. The store's Search & Discovery filters carry the sizes as 50 mm BANDS
   (custom.product_width_band / _depth_band / _height_band, values "0600-0649"). snippets/ce-facets.liquid prints, per
   measurement, a [data-ce-dim] block: the bands as real tick boxes (name = the filter's param, data-lo / data-hi /
   data-n = the band's edges and its live product count) plus a two-handle slider and two mm boxes. This file:
     - moves the handles in 50 mm steps (band edges), paints the fill, a small histogram of where the stock sits and
       the true range ("600-899 mm"), and the live count (the band counts added up: Shopify counts a filter's own
       values as if it were not applied, so the sum is exact);
     - on release (or Enter in a box) ticks EVERY band that lies WHOLLY inside the range ("up to 900" never shows a
       940 mm unit; the range is then reported as what it really is, 600-899 mm) and submits the form. On a
       collection page assets/ce-collection.js catches the submit and applies it in place; anywhere else the form
       simply loads the page with the same parameters;
     - a handle parked on the end of its track means "no limit that end"; both ends parked = the filter is off;
     - never sends a range that holds no products, and never an address over the 16 KB limit the live site hit
       (HTTP 414 at 16,416 characters): it says so instead.
   No dependencies; deferred; idempotent; window.CEDims.bind(root) after new markup (ce-collection.js calls it after
   every swap).
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CEDims) return;

  const URL_BUDGET = 15000;      // the live wall: 16,416 characters answered 414. Leave room for the rest.
  const MAX_BARS = 44;
  const fmt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  class Dim {
    constructor(el) {
      this.el = el;
      this.lo = parseInt(el.dataset.lo, 10) || 0;
      this.hix = parseInt(el.dataset.hi, 10) || 1;          // exclusive end (a band edge)
      this.step = parseInt(el.dataset.step, 10) || 50;
      this.name = (el.dataset.name || 'size').toLowerCase();
      this.r1 = el.querySelector('[data-ce-dim-range="min"]');
      this.r2 = el.querySelector('[data-ce-dim-range="max"]');
      this.f1 = el.querySelector('[data-ce-dim-field="min"]');
      this.f2 = el.querySelector('[data-ce-dim-field="max"]');
      this.sayEl = el.querySelector('[data-ce-dim-say]');
      this.countEl = el.querySelector('[data-ce-dim-count]');
      this.plot = el.querySelector('[data-ce-dim-plot]');
      this.slider = el.querySelector('.ce-fdim__slider');
      if (!this.r1 || !this.r2) return;
      this.bands = [...el.querySelectorAll('input[type="checkbox"][data-lo]')].map((cb) => ({
        cb, lo: parseInt(cb.dataset.lo, 10), hi: parseInt(cb.dataset.hi, 10), n: parseInt(cb.dataset.n, 10) || 0
      }));
      const on = this.bands.filter((b) => b.cb.checked);
      this.from = on.length ? Math.min(...on.map((b) => b.lo)) : this.lo;
      this.to = on.length ? Math.max(...on.map((b) => b.hi)) + 1 : this.hix;
      this.applied = [this.from, this.to];
      this.timer = 0;
      this.drawPlot();
      this.listen();
      this.paint();
      el.classList.add('is-ready');
    }

    /* the bands inside a range: wholly inside (an open end takes everything past it) */
    inside(from, to) {
      const openLo = from <= this.lo, openHi = to >= this.hix;
      return this.bands.filter((b) => (openLo || b.lo >= from) && (openHi || b.hi < to));
    }

    count(from, to) { return this.inside(from, to).reduce((a, b) => a + b.n, 0); }

    whole() { return this.from <= this.lo && this.to >= this.hix; }

    phrase() {
      if (this.whole()) return 'Any ' + this.name;
      const a = fmt(this.from), b = fmt(this.to - 1);
      if (this.from <= this.lo) return 'Up to ' + b + ' mm';
      if (this.to >= this.hix) return a + ' mm and over';
      return a + '–' + b + ' mm';
    }

    drawPlot() {
      if (!this.plot) return;
      const steps = Math.max(1, Math.round((this.hix - this.lo) / this.step));
      const per = Math.max(1, Math.ceil(steps / MAX_BARS));
      const nBars = Math.ceil(steps / per);
      const sums = new Array(nBars).fill(0);
      this.bands.forEach((b) => {
        const i = Math.min(nBars - 1, Math.max(0, Math.floor((b.lo - this.lo) / this.step / per)));
        sums[i] += b.n;
      });
      const peak = Math.max(1, ...sums);
      this.plot.textContent = '';
      this.bars = sums.map((v, i) => {
        const bar = document.createElement('i');
        // a soft square-root scale, so one huge band does not flatten the rest; an empty slot stays a hairline
        bar.style.setProperty('--h', v ? (12 + 88 * Math.sqrt(v / peak)).toFixed(1) + '%' : '0%');
        bar.dataset.a = String(this.lo + i * per * this.step);
        bar.dataset.b = String(Math.min(this.hix, this.lo + (i + 1) * per * this.step));
        this.plot.appendChild(bar);
        return bar;
      });
    }

    paint() {
      const span = Math.max(1, this.hix - this.lo);
      if (this.slider) {
        this.slider.style.setProperty('--a', ((this.from - this.lo) / span).toFixed(4));
        this.slider.style.setProperty('--b', ((this.hix - this.to) / span).toFixed(4));
      }
      this.r1.value = String(this.from);
      this.r2.value = String(this.to);
      // two handles on one spot: the one that can still move is on top
      this.r1.style.zIndex = (this.from - this.lo) / span > 0.5 ? '4' : '3';
      this.r1.setAttribute('aria-valuetext', this.from <= this.lo ? 'no minimum' : fmt(this.from) + ' mm');
      this.r2.setAttribute('aria-valuetext', this.to >= this.hix ? 'no maximum' : fmt(this.to - 1) + ' mm');
      if (this.f1 && document.activeElement !== this.f1) this.f1.value = this.from <= this.lo ? '' : String(this.from);
      if (this.f2 && document.activeElement !== this.f2) this.f2.value = this.to >= this.hix ? '' : String(this.to - 1);
      if (this.bars) this.bars.forEach((bar) => bar.classList.toggle('is-in', +bar.dataset.b > this.from && +bar.dataset.a < this.to));
      if (this.sayEl) this.sayEl.textContent = this.phrase();
      const changed = this.from !== this.applied[0] || this.to !== this.applied[1];
      this.el.classList.toggle('is-on', !this.whole());
      this.el.classList.toggle('is-dirty', changed);
      if (this.countEl) {
        let msg = '';
        if (!this.whole()) {
          const n = this.count(this.from, this.to);
          msg = n === 0 ? 'No products this size' : fmt(n) + ' product' + (n === 1 ? '' : 's');
        }
        if (this.note) { msg = this.note; this.note = ''; }
        this.countEl.textContent = msg;
        this.countEl.classList.toggle('is-none', !this.whole() && this.count(this.from, this.to) === 0);
      }
    }

    listen() {
      const onRange = (e) => {
        let a = parseInt(this.r1.value, 10), b = parseInt(this.r2.value, 10);
        // the handles never cross: at least one step apart
        if (e && e.target === this.r1 && a > b - this.step) a = b - this.step;
        if (e && e.target === this.r2 && b < a + this.step) b = a + this.step;
        this.from = Math.max(this.lo, Math.min(a, this.hix - this.step));
        this.to = Math.min(this.hix, Math.max(b, this.lo + this.step));
        this.paint();
      };
      this.r1.addEventListener('input', onRange);
      this.r2.addEventListener('input', onRange);
      // apply on release (pointer) or after a short pause (arrow keys): each apply is a page fetch
      const later = () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.commit(), 260); };
      this.r1.addEventListener('change', later);
      this.r2.addEventListener('change', later);
      [this.f1, this.f2].forEach((f) => {
        if (!f) return;
        f.addEventListener('change', () => { this.fromFields(f); this.commit(); });
        f.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); this.fromFields(f); this.commit(); }
        });
        f.addEventListener('input', () => { f.value = f.value.replace(/[^\d]/g, '').slice(0, 5); });
      });
    }

    /* a typed size: From rounds UP to a band edge, To rounds DOWN (only whole bands inside the range); an empty box
       means no limit on that end */
    fromFields(changed) {
      const st = this.step;
      const v1 = this.f1 && this.f1.value.trim() !== '' ? parseInt(this.f1.value, 10) : NaN;
      const v2 = this.f2 && this.f2.value.trim() !== '' ? parseInt(this.f2.value, 10) : NaN;
      let from = isNaN(v1) ? this.lo : Math.ceil(v1 / st) * st;
      let to = isNaN(v2) ? this.hix : Math.floor((v2 + 1) / st) * st;
      from = Math.max(this.lo, Math.min(from, this.hix - st));
      to = Math.min(this.hix, Math.max(to, this.lo + st));
      if (to <= from) { if (changed === this.f1) to = Math.min(this.hix, from + st); else from = Math.max(this.lo, to - st); }
      this.from = from;
      this.to = to;
      if (this.f1) this.f1.value = this.from <= this.lo ? '' : String(this.from);
      if (this.f2) this.f2.value = this.to >= this.hix ? '' : String(this.to - 1);
      this.paint();
    }

    commit() {
      clearTimeout(this.timer);
      if (this.from === this.applied[0] && this.to === this.applied[1]) return;
      const whole = this.whole();
      const pick = whole ? [] : this.inside(this.from, this.to);
      if (!whole && pick.reduce((a, b) => a + b.n, 0) === 0) { this.paint(); return; }   // nothing that size: say so, send nothing
      const before = this.bands.map((b) => b.cb.checked);
      this.bands.forEach((b) => { b.cb.checked = pick.includes(b); });
      const form = this.el.closest('form');
      if (form) {
        const qs = new URLSearchParams(new FormData(form)).toString();
        if ((window.location.pathname + '?' + qs).length > URL_BUDGET) {
          this.bands.forEach((b, i) => { b.cb.checked = before[i]; });
          this.note = 'That range is too wide to send in one go: try a narrower one.';
          this.paint();
          return;
        }
      }
      this.applied = [this.from, this.to];
      this.el.classList.add('is-sent');
      if (!form) return;
      const ev = new Event('submit', { bubbles: true, cancelable: true });
      if (form.dispatchEvent(ev)) form.submit();   // nobody handled it in place: an ordinary page load
    }
  }

  const bind = (root) => {
    const scope = root || document;
    const els = scope.matches && scope.matches('[data-ce-dim]') ? [scope] : [...scope.querySelectorAll('[data-ce-dim]')];
    els.forEach((el) => { if (!el.ceDim) el.ceDim = new Dim(el); });
  };

  window.CEDims = { bind };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bind(document));
  else bind(document);
  document.addEventListener('shopify:section:load', (e) => bind(e.target));
})();
